import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useEffect, useState } from 'react'
import { EventRequestDto } from "../types";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { getEventById, saveEvent } from "../api/eventAPI";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { getEventFormSchema } from "../schema/eventSchema";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function NewEvent() {
    const { t } = useTranslation(['common','event']);
    const navigate = useNavigate();

    const { eventId } = useParams<{ eventId: string }>();
    const isEditMode = Boolean(eventId);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [eventForm, setEventForm] = useState<EventRequestDto>({
        id: 0,
        description: "",
        endDate: "",
        startDate: "",
    })

    const changeInputField = (field: keyof EventRequestDto) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setEventForm({
            ...eventForm,
            [field]: e.target.value
        });
    };

    useEffect(() => {
        if (eventId) {
            getEventById(eventId)
                .then(data => setEventForm(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [eventId]);

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getEventFormSchema(t);

        try {
            await schema.validate(eventForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }


        try {
            if (isEditMode && eventId) {
                navigate('/admin/event', {
                    state: { message: t("event:modal.eventEndSuccessMissage"), shouldRefresh: true }
                });
            } else {
                const payload = {
                    ...eventForm,
                    startDate: eventForm.startDate ? new Date(eventForm.startDate).toISOString() : "",
                    endDate: eventForm.endDate ? new Date(eventForm.endDate).toISOString() : null,
                };

                await saveEvent(payload);
                navigate('/admin/event', {
                    state: { message: t("event:newEventSaved"), shouldRefresh: true }
                });
            }

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });

        }
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{eventId ? t("Edita esdeveniment") : t("event:buttonNewEvent")}</h2>
                </Col>
                <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/admin/event')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonCancel") : t("common:buttons.buttonBack")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("event:headerTableEventDescription")} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={eventForm.description}
                                        onChange={changeInputField('description')}
                                        isInvalid={!!errors["description"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group className="mb-3">
                                    <InputDatePicker
                                        value={eventForm.startDate ? new Date(eventForm.startDate) : null}
                                        onChange={(date: Date | null) => {
                                            setEventForm(prev => ({
                                                ...prev,
                                                startDate: date ? date.toISOString() : null
                                            }));
                                        }}
                                        label={t("event:headerTableEventDateInicial")}
                                        hasError={'startDate' in errors && !!errors.startDate}
                                        errorMessage={errors?.startDate}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group className="mb-3">
                                    <InputDatePicker
                                        value={eventForm.endDate ? new Date(eventForm.endDate) : null}
                                        onChange={(date: Date | null) => {
                                            setEventForm(prev => ({
                                                ...prev,
                                                endDate: date ? date.toISOString() : null
                                            }));
                                        }}
                                        label={t("event:headerTableEventDateFinal")}
                                        hasError={'endDate' in errors && !!errors.endDate}
                                        errorMessage={errors?.endDate}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}



